import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MessageSquareHeart, RefreshCw, Mail, Loader2, Megaphone, FileDown, Users, CheckSquare, Square, ShieldOff,
} from 'lucide-react';
import RexCard from './RexCard';
import { groupRexByCohort, cohortLabel, NO_COHORT_ID } from '../../../utils/rexSynthesis';

const API = process.env.REACT_APP_BACKEND_URL;

/** Admin — onglet REX : retours d'expérience classés par cohorte + synthèse anonymisée. */
export default function RexTab({ token, cohorts = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openVideo, setOpenVideo] = useState(null);
  const [cohortId, setCohortId] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [viewCohort, setViewCohort] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pdfBusy, setPdfBusy] = useState(false);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const fetchRex = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/rex`, { headers });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (err) { console.warn(err.message); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchRex(); }, [fetchRex]);

  const toggleRead = async (rex) => {
    setBusyId(rex.id);
    try {
      await fetch(`${API}/api/admin/rex/${rex.id}/toggle-read`, { method: 'POST', headers });
      await fetchRex();
    } catch {}
    setBusyId(null);
  };

  const remove = async (rex) => {
    if (!window.confirm(`Supprimer le REX de ${rex.holder_name || rex.license_key} ?`)) return;
    setBusyId(rex.id);
    try {
      await fetch(`${API}/api/admin/rex/${rex.id}`, { method: 'DELETE', headers });
      await fetchRex();
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(rex.id); return n; });
    } catch {}
    setBusyId(null);
  };

  const unread = items.filter((i) => !i.read_at).length;
  const groups = useMemo(() => groupRexByCohort(items, cohorts), [items, cohorts]);
  const visibleGroups = viewCohort === 'all' ? groups : groups.filter((g) => g.id === viewCohort);
  const visibleItems = visibleGroups.flatMap((g) => g.items);
  const selectedItems = items.filter((r) => selectedIds.has(r.id));

  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectGroup = (g, on) => setSelectedIds((prev) => { const n = new Set(prev); g.items.forEach((r) => (on ? n.add(r.id) : n.delete(r.id))); return n; });

  const exportSynthesis = async (list, title, slug) => {
    if (list.length === 0 || pdfBusy) return;
    setPdfBusy(true);
    try {
      const { default: generateRexSynthesisPDF } = await import('../../../utils/generateRexSynthesisPDF');
      generateRexSynthesisPDF({ items: list, title, filename: `hst-synthese-rex-${slug}.pdf` });
    } catch (err) {
      console.warn('[rex] synthesis PDF failed', err);
      window.alert('Impossible de générer la synthèse PDF.');
    }
    setPdfBusy(false);
  };
  const slugify = (s) => String(s || 'rex').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);

  const requestCohort = async () => {
    if (!cohortId || requesting) return;
    setRequesting(true);
    setRequestResult(null);
    try {
      const res = await fetch(`${API}/api/admin/rex/request`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ master_license_id: cohortId }),
      });
      const data = await res.json().catch(() => ({}));
      setRequestResult(res.ok
        ? { ok: true, count: data.requested, emails: data.emails_sent || 0 }
        : { ok: false, error: data.detail || `HTTP ${res.status}` });
    } catch {
      setRequestResult({ ok: false, error: 'Erreur réseau' });
    }
    setRequesting(false);
  };

  return (
    <div className="space-y-4" data-testid="rex-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-foreground flex items-center gap-2">
          <MessageSquareHeart className="w-5 h-5 text-accent" /> Retours d'expérience apprenants
          {unread > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent text-white font-bold" data-testid="rex-unread-count">{unread} non lu{unread > 1 ? 's' : ''}</span>
          )}
        </h2>
        <button onClick={fetchRex} className="p-2.5 border border-ink-600 rounded-lg hover:bg-ink-700/40" data-testid="rex-refresh">
          <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="bg-ink-800 border border-ink-600 rounded-xl p-4" data-testid="rex-cohort-request">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-accent" /> Demander les REX d'une cohorte entière
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={cohortId}
            onChange={(e) => { setCohortId(e.target.value); setRequestResult(null); }}
            className="flex-1 min-w-[240px] bg-background border border-ink-600 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            data-testid="rex-cohort-select"
          >
            <option value="">— Choisir une cohorte (responsable) —</option>
            {cohorts.map((c) => (
              <option key={c.master.id} value={c.master.id}>
                {`${cohortLabel(c)} (${c.children.length} apprenant${c.children.length > 1 ? 's' : ''})`}
              </option>
            ))}
          </select>
          <button
            onClick={requestCohort}
            disabled={!cohortId || requesting}
            className="flex items-center gap-2 bg-accent text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="rex-cohort-request-btn"
          >
            {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquareHeart className="w-3.5 h-3.5" />}
            Demander les REX
          </button>
        </div>
        {requestResult && (
          <p className={`text-xs mt-2 font-medium ${requestResult.ok ? 'text-money' : 'text-danger'}`} data-testid="rex-cohort-request-result">
            {requestResult.ok
              ? `✓ ${requestResult.count} apprenant${requestResult.count > 1 ? 's' : ''} invité${requestResult.count > 1 ? 's' : ''} — ${requestResult.emails} email${requestResult.emails > 1 ? 's' : ''} d'invitation envoyé${requestResult.emails > 1 ? 's' : ''}. Ils verront aussi l'invitation à leur prochaine session de jeu.`
              : `Échec : ${requestResult.error}`}
          </p>
        )}
      </div>

      {items.length > 0 && (
        <div className="bg-ink-800 border border-ink-600 rounded-xl p-4 space-y-3" data-testid="rex-synthesis-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-accent" /> Classer par cohorte
            </p>
            <select
              value={viewCohort}
              onChange={(e) => setViewCohort(e.target.value)}
              className="min-w-[240px] bg-background border border-ink-600 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              data-testid="rex-view-cohort"
            >
              <option value="all">{`Toutes les cohortes (${items.length} REX)`}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{`${g.label} (${g.items.length} REX)`}</option>
              ))}
            </select>
            <span className="text-[11px] text-slate-500" data-testid="rex-selection-count">
              {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </span>
            {selectedIds.size > 0 && (
              <button onClick={() => setSelectedIds(new Set())} className="text-[11px] text-slate-400 hover:text-slate-200 underline" data-testid="rex-clear-selection">
                Vider la sélection
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => exportSynthesis(selectedItems, `Synthèse REX — sélection (${selectedItems.length})`, 'selection')}
              disabled={selectedItems.length === 0 || pdfBusy}
              className="flex items-center gap-2 bg-money text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="rex-synthesis-selection-btn"
            >
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
              Synthèse PDF de la sélection ({selectedItems.length})
            </button>
            {viewCohort !== 'all' && visibleItems.length > 0 && (
              <button
                onClick={() => exportSynthesis(visibleItems, `Synthèse REX — ${visibleGroups[0]?.label || 'cohorte'}`, slugify(visibleGroups[0]?.label))}
                disabled={pdfBusy}
                className="flex items-center gap-2 border border-money/50 text-money px-4 py-2 rounded-lg text-xs font-bold hover:bg-money/10 disabled:opacity-40"
                data-testid="rex-synthesis-cohort-btn"
              >
                <FileDown className="w-3.5 h-3.5" /> Synthèse PDF de la cohorte ({visibleItems.length})
              </button>
            )}
            <span className="text-[10px] text-slate-500 flex items-center gap-1"><ShieldOff className="w-3 h-3" /> Document anonymisé : ni nom, ni clé de licence (« Apprenant n°1, n°2… »).</span>
          </div>
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="bg-ink-800 border border-ink-600 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="rex-empty">
          Aucun REX reçu pour le moment. Utilisez le bouton « Demander un REX » dans l'onglet Licences
          pour inviter un apprenant (ou toute une cohorte) à témoigner.
        </div>
      )}

      {visibleGroups.map((g) => {
        const allSelected = g.items.every((r) => selectedIds.has(r.id));
        return (
          <section key={g.id} className="space-y-3" data-testid={`rex-group-${g.id === NO_COHORT_ID ? 'none' : g.id}`}>
            <div className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-ink-600" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 flex items-center gap-1.5" data-testid={`rex-group-header-${g.id === NO_COHORT_ID ? 'none' : g.id}`}>
                <Users className="w-3 h-3" /> {g.label} · {g.items.length} REX
              </span>
              <button
                onClick={() => selectGroup(g, !allSelected)}
                className="text-[10px] text-accent hover:underline flex items-center gap-1"
                data-testid={`rex-group-select-${g.id === NO_COHORT_ID ? 'none' : g.id}`}
              >
                {allSelected ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
              <div className="h-px flex-1 bg-ink-600" />
            </div>
            {g.items.map((rex) => (
              <RexCard
                key={rex.id}
                rex={rex}
                busy={busyId === rex.id}
                selected={selectedIds.has(rex.id)}
                onToggleSelect={toggleSelect}
                onToggleRead={toggleRead}
                onRemove={remove}
                openVideo={openVideo === rex.id}
                onOpenVideo={setOpenVideo}
              />
            ))}
          </section>
        );
      })}

      <p className="text-[10px] text-slate-600 flex items-center gap-1.5">
        <Mail className="w-3 h-3" /> Chaque REX est aussi envoyé par email à l'adresse configurée (REX_NOTIFY_EMAIL).
      </p>
    </div>
  );
}
